import { useCallback, useMemo, useState } from 'react';

export type Cookie = Record<string, any>;
export type CookiePersistence = 'session' | { expiresInDays: number };

const EMPTY_COOKIE: Cookie = {};

type CookieState<T> = readonly [
  T,
  (value: T, persistence: CookiePersistence) => void,
  () => void
];

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const setDocumentCookie = (value: string) => {
  document.cookie = `${value}; path=/; SameSite=Lax${
    location.protocol === 'https:' ? '; Secure' : ''
  }`;
};

const writeCookie = (name: string, value: string, days?: number) => {
  const expiryDate = days ? new Date(Date.now() + days * ONE_DAY_MS) : null;
  const expires = expiryDate ? `; expires=${expiryDate.toUTCString()}` : '';
  setDocumentCookie(`${name}=${encodeURIComponent(value)}${expires}`);
};

const expireCookie = (name: string) => {
  setDocumentCookie(`${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`);
};

const readCookie = <T>(name: string, defaultValue: T): T => {
  const cookie = getCookie(name);
  if (!cookie) return defaultValue;

  try {
    return JSON.parse(cookie);
  } catch {
    console.warn('Bad Cookie : ', name);
    return defaultValue;
  }
};

// Overloaded so a plain object cookie needs no defaultValue, while any other
// type must supply one. {} is only a valid "empty" value when T is Cookie.
function useCookie(cookieName: string): CookieState<Cookie>;
function useCookie<T>(cookieName: string, defaultValue: T): CookieState<T>;
function useCookie<T = Cookie>(
  cookieName: string,
  defaultValue?: T
): CookieState<T> {
  const actualDefault = (defaultValue === undefined
    ? EMPTY_COOKIE
    : defaultValue) as T;

  const [cookie, setCookie] = useState<T>(() =>
    readCookie(cookieName, actualDefault)
  );

  const saveCookie = useCallback(
    (value: T, persistence: CookiePersistence) => {
      const expiresInDays =
        persistence === 'session' ? undefined : persistence.expiresInDays;
      writeCookie(cookieName, JSON.stringify(value), expiresInDays);
      setCookie(value);
    },
    [cookieName]
  );

  const removeCookie = useCallback(() => {
    expireCookie(cookieName);
    setCookie(actualDefault);
  }, [cookieName, actualDefault]);

  return useMemo(() => [cookie, saveCookie, removeCookie] as const, [
    cookie,
    saveCookie,
    removeCookie,
  ]);
}

export default useCookie;
